import { http as atsHttp } from '@/lib/ats-https';

// Org-master enums (Job Title Code, Job Level Code, …) come as {code, name};
// static enums (Gender, Yes/No) stay plain strings. The form shows `name` but
// must submit `code` — Darwinbox validates against the code.
export interface DarwinboxEnumOption {
  code: string;
  name: string;
}

// The per-account form builder's kind vocabulary (kula-ai/core #5926). BE folds
// each kind into the legacy `type` above so this form keeps rendering unchanged,
// and sends `kind` alongside only for fields defined in that builder — a field
// still on the old hardcoded WeWork list carries no `kind` at all.
export type DarwinboxFieldKind =
  | 'short_text'
  | 'long_text'
  | 'single_select'
  | 'multi_select'
  | 'yes_no'
  | 'date'
  | 'number'
  | 'url';

export interface DarwinboxFormField {
  id: string;
  label: string;
  type: 'string' | 'date' | 'enum';
  required: boolean;
  // Tenant's configured Darwinbox date format (e.g. "DD-MM-YYYY"), present only
  // on date fields — see GlobalIntegration::DARWINBOX_DATE_FORMATS.
  format?: string;
  // Missing/empty options on an enum field means Darwinbox was unreachable —
  // render free text so the push still works.
  options?: Array<string | DarwinboxEnumOption>;
  // An array for a multi_select field's prefilled value; a single value
  // otherwise.
  value: string | string[] | null;
  kind?: DarwinboxFieldKind;
  // True when the admin locked this field in the builder — Darwinbox ignores
  // any recruiter input for it and always uses the mapping/default instead.
  locked?: boolean;
  placeholder?: string | null;
}

interface GetFormParams {
  applicationId: string | number;
  integrationType: string;
}

const getDarwinboxEmployeeForm = async ({
  applicationId,
  integrationType,
}: GetFormParams): Promise<DarwinboxFormField[]> => {
  // Match the existing string-concat pattern used by other HRIS endpoints
  // (see hris_field_mappings) so BE sees the unencoded integration_type.
  const response = await atsHttp.get(
    `/ats_applications/${applicationId}/hris_employees/new?integration_type=${integrationType}`
  );
  return response?.data?.fields || [];
};

export const DARWINBOX_HRIS_API_SERVICE = {
  getDarwinboxEmployeeForm,
};
